import { useMutation } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { ENVIRONMENT_QUERY } from "components/Environment/constants";
import {
  AddStackConfigGqlResponse,
  AddStackConfigGqlVariables,
  UpdateStackConfigGql,
  UpdateStackConfigGqlVariables,
} from "components/Environment/edit/types";
import { EnvironmentType } from "components/Environment/types";
import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElement, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";

import { ADD_STACK_CONFIG, UPDATE_STACK_CONFIG } from "../gql";

type DrawerEnvVariableProps = {
  variable?: ConfigElement;
  stackId: string;
  type: EnvironmentType;
};

type FormValues = {
  id: string;
  value: string;
  description: string;
  writeOnly: boolean;
};

const DrawerEnvVariable = createDrawer(({ variable, stackId, type }: DrawerEnvVariableProps) => {
  const isEdit = !!variable;
  const drawer = useModal();
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [addConfig, { loading }] = useMutation<
    AddStackConfigGqlResponse,
    Partial<AddStackConfigGqlVariables>
  >(ADD_STACK_CONFIG, {
    refetchQueries: [ENVIRONMENT_QUERY[type]],
    variables: { stackId },
  });

  const [updateConfig, { loading: updating }] = useMutation<
    UpdateStackConfigGql,
    UpdateStackConfigGqlVariables
  >(UPDATE_STACK_CONFIG, {
    refetchQueries: [ENVIRONMENT_QUERY[type]],
    variables: { stackId },
  });

  const builderForm = useForm<FormValues>({
    defaultValues: {
      id: variable?.id ?? "",
      value: variable?.value ?? "",
      description: variable?.description ?? "",
      writeOnly: variable?.writeOnly ?? false,
    },
    shouldFocusError: true,
    mode: "onChange",
  });

  const closeDrawer = () => {
    drawer.resolve();
    drawer.hide();
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    register,
  } = builderForm;

  const onSubmit = (formData: FormValues) => {
    const promise = isEdit ? updateConfig : addConfig;
    return promise({
      variables: {
        input: {
          ...formData,
          type: ConfigType.EnvironmentVariable,
          fileMode: null,
        },
      },
    })
      .then((data) => {
        if (data.data?.stackConfigAdd.id) {
          reportSuccess({
            message: `Environment variable was ${isEdit ? "updated" : "created"} successfully`,
          });
          closeDrawer();
        }
      })
      .catch(onError);
  };

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle
              title={isEdit ? "Edit environment variable" : "New environment variable"}
            />
            <DrawerCloseIcon />
          </DrawerHeader>

          <DrawerBody gap="x-large" fullHeight>
            <FormField label="Name" error={errors?.id?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Name"
                  disabled={!!variable}
                  error={!!errors?.id}
                  {...register("id", { required: "Name field is required." })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <FormField label="Value" error={errors?.value?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Value"
                  error={!!errors?.value}
                  {...register("value", { required: "Value field is required." })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
            <Controller
              name="writeOnly"
              control={control}
              render={({ field }) => (
                <FormToggleField
                  variant="switch"
                  onChange={field.onChange}
                  checked={field.value}
                  title="Mark as secret"
                  description="Secret values are only made available to Runs and Tasks and are not accessible in the web GUI and through the API. Marking variables as a secret is a recommended option."
                />
              )}
            />

            <FormField label="Description" isOptional error={errors?.description?.message}>
              {({ ariaInputProps }) => (
                <Textarea
                  minRows={1}
                  maxRows={15}
                  placeholder="Enter description here..."
                  error={!!errors.description}
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button variant="primary" type="submit" disabled={loading || updating}>
                  {isEdit ? "Save" : "Create"}
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showEnvVariableDrawer = createDrawerTrigger(DrawerEnvVariable);
